import React, { useState, useEffect } from 'react';
import Modal from '../../common/Modal';
import { useAlert } from '../../../contexts/AlertContext';
import { CONTRACT_ACTIONS, PERSON_ACTIONS, contractIdentityLabel } from '../../../constants/contractActions';
import { findPendingRenewalEvent } from '../../../constants/employmentEpisodes';
import { contractLineLabel, openContractsOf, previewEndEmployment, resignationRefusal } from '../../../utils/endEmploymentPreview';
import { contractActionFacts } from '../../../utils/contractActionFacts';
import EmployeeResign from '../EmployeeResign';
import EmployeeMarkNoShow from '../EmployeeMarkNoShow';
import EmployeeStartDelayed from '../EmployeeStartDelayed';
import EmployeeHireWithdraw from '../EmployeeHireWithdraw';
import EmployeeResignWithdraw from '../EmployeeResignWithdraw';
import EmployeeChangeContractEnd from '../EmployeeChangeContractEnd';
import EmployeeCancelContract from '../EmployeeCancelContract';
import EmployeeAddContractEnd from '../EmployeeAddContractEnd';
import EmployeeCancelScheduledRenewal from '../EmployeeCancelScheduledRenewal';
import EmployeeVoidContract from '../EmployeeVoidContract';
import ContractNumberModal from './ContractNumberModal';
import {
  startDelayed,
  withdrawResignation,
  resign,
  changeContractEnd,
  cancelContract,
  cancelScheduledRenewal,
  endEmployment,
  voidContract,
  END_EMPLOYMENT_KINDS
} from '../../../services/event-handlers/employment';

/**
 * ADR-113 §5 — the ONE implementation of every contract-addressed lifecycle
 * modal. The Contracts tab (its authoritative home) and the card menu's
 * retained shortcuts both mount THIS component; neither carries its own copy
 * of the modal state or the submit logic. The bodies stay the existing
 * per-verb components (EmployeeResign, EmployeeAddContractEnd, …); the
 * submit handlers here are moved verbatim from EmployeesRepository/index.jsx,
 * where they lived while the verbs were card-homed.
 *
 * `episode` is the acting contract when the caller knows it (the Contracts
 * tab always does). Pre-ADR-144-phase-2 the payloads stay employee-addressed
 * — the placement is the behaviour-neutral slice (ADR-113 §10) — but the
 * episode already scopes the "Already scheduled" panel and the title.
 */

/**
 * ADR-171 U4 (ADR-159 §5) — End Employment acts on EVERY contract the person
 * holds; with more than one, the confirmation says what happens to each.
 */
const EndEmploymentContracts = ({ kind, contracts, resignationDate }) => {
  if (contracts.length < 2) {
    return null;
  }
  const rows = previewEndEmployment({ kind, contracts, resignationDate });
  return (
    <div style={{ marginTop: 16 }}>
      <p style={{ margin: '0 0 4px' }}>
        <strong>This ends the employment — every contract this individual holds:</strong>
      </p>
      <ul style={{ margin: 0 }}>
        {rows.map((row) => (
          <li key={row.id}>
            {row.label}{row.isPrimary ? ' (primary)' : ''}{' — '}{row.outcome}
          </li>
        ))}
      </ul>
    </div>
  );
};

const ContractActionModals = ({ action, employee, episode = null, onClose, onCompleted }) => {
  const { showAlert } = useAlert();
  const [formData, setFormData] = useState(null);
  const [formValid, setFormValid] = useState(false);
  const [resignStep, setResignStep] = useState(0);
  const [resignStepHandlers, setResignStepHandlers] = useState(null);
  // ADR-171 C2 / ADR-153 R1 — the server refused to end the PRIMARY before
  // another contract: show them and offer "end these too"
  const [outlivingPrompt, setOutlivingPrompt] = useState(null); // { contracts }

  // Fresh form state per opening — the bodies re-initialize as invalid on
  // mount, and this clears the two-step resign machinery between openings.
  useEffect(() => {
    setFormData(null);
    setFormValid(false);
    setResignStep(0);
    setResignStepHandlers(null);
    setOutlivingPrompt(null);
  }, [action, employee?.id]);

  if (!action || !employee) return null;

  // ADR-174 §5 — the Contract Number verb has its own body and endpoint (not
  // an employment event); routed here so the tab has one modal router.
  if (action === CONTRACT_ACTIONS.SET_CONTRACT_NUMBER) {
    return (
      <ContractNumberModal employee={employee} episode={episode} onClose={onClose} onCompleted={onCompleted} />
    );
  }

  // ADR-155 §6 — the contract every lifecycle verb below addresses: the tab's
  // acting episode, else (card shortcuts) the person's primary — the pointer
  // the API exposes, which is the only open contract until ADR-144 phase 5.
  // The person verb Resign deliberately sends NO episode (ADR-149): it ends
  // employment, and becomes the phase-5 fan-out verb.
  // ADR-187 D4 — never guess the contract: the primary stands in only when the
  // person holds at most one open contract. With more, no episode is sent and
  // the server's EPISODE_REQUIRED refusal (handled below) applies.
  const fallbackEpisodeId = openContractsOf(employee).length <= 1
    ? (employee.primaryEmploymentEpisodeId || employee.currentEmploymentEpisodeId || null)
    : null;
  const episodeId = episode?.id || fallbackEpisodeId;
  // ADR-187 D1/D2 — the acting contract's own facts (start, scheduled end,
  // number, source, scheduled renewal), never the person view's (= primary's).
  const contractFacts = contractActionFacts(employee, episode || (episodeId ? { id: episodeId } : null));
  const address = episodeId ? { employmentEpisodeId: episodeId } : {};
  const identity = contractIdentityLabel(employee, episode);
  const suffix = identity ? ` — ${identity}` : '';
  const name = employee.fullName || 'Individual';
  const openContracts = openContractsOf(employee);
  // ADR-185 §8 — with more than one open contract the resign wizard gains a
  // Review Contracts step: Details → Contracts → Projects.
  const showContractsStep = openContracts.length > 1;
  const resignLastStep = showContractsStep ? 2 : 1;

  const handleValidChange = (isValid, data) => {
    setFormValid(isValid);
    setFormData(data);
  };

  const handleResignStepChange = (step, handlers) => {
    setResignStep(step);
    if (handlers) {
      setResignStepHandlers(handlers);
    }
  };

  const finish = async (message) => {
    showAlert(message, 'success');
    await onCompleted();
  };

  const submitResign = async () => {
    // Every step but the last advances; the last submits.
    if (resignStep < resignLastStep && resignStepHandlers?.handleNext) {
      resignStepHandlers.handleNext();
      return;
    }
    // ADR-159 §5 — End Employment (RESIGN): every contract the person holds
    await endEmployment({
      kind: END_EMPLOYMENT_KINDS.RESIGN,
      employeeId: employee.id,
      resignationDate: formData.resignationDate,
      resignationReason: formData.resignationReason === 'Other'
        ? formData.resignationReasonDetails
        : formData.resignationReason
    });
    await finish(`Resignation submitted for ${name}`);
  };

  const handleResignCancel = () => {
    // Any step but the first backs up instead of closing.
    if (resignStep > 0 && resignStepHandlers?.handleBack) {
      resignStepHandlers.handleBack();
      return;
    }
    onClose();
  };

  const CONFIG = {
    // ADR-149 §5.3 — a person verb hosted by the same router. Its title
    // carries no contract identity: the modal is about the person, and
    // naming a contract in it would re-assert exactly what §1 reversed.
    [PERSON_ACTIONS.RESIGN]: {
      title: `Submit Resignation for ${name}`,
      maxWidth: 'lg',
      submitText: resignStep < resignLastStep ? 'Next' : 'Submit Resignation',
      cancelText: resignStep > 0 ? 'Back' : 'Cancel',
      onCancel: handleResignCancel,
      onSubmit: submitResign,
      failMessage: 'Failed to submit resignation',
      // ADR-185 D2 — the server would refuse this date: say so, don't go on
      blocked: Boolean(resignationRefusal({ contracts: openContracts, resignationDate: formData?.resignationDate })),
      // ADR-185 D3 — a person verb: no contract scopes its body. The contracts
      // it ends are on their own step (EmployeeResign → Review Contracts).
      body: (
        <EmployeeResign
          employee={employee}
          contracts={openContracts}
          showContractsStep={showContractsStep}
          onValidChange={handleValidChange}
          onStepChange={handleResignStepChange}
        />
      )
    },
    // ADR-158 §7.5 — a person verb (like Resign above): no contract identity
    // in the title, and NO episode on the payload. ADR-159 §5: it is End
    // Employment (NO_SHOW); the server applies it to every contract.
    [PERSON_ACTIONS.MARK_NO_SHOW]: {
      title: `Mark ${name} as No-Show`,
      maxWidth: 'md',
      submitText: 'Mark No Show',
      onSubmit: async () => {
        // The scheduled end is withdrawn first by the server when one exists
        // (ADR-136 §2a #3, now decided per contract — ADR-159 §5).
        await endEmployment({
          kind: END_EMPLOYMENT_KINDS.NO_SHOW,
          employeeId: employee.id,
          ...(formData?.plannedStart ? { plannedStart: formData.plannedStart } : {}),
          ...(formData?.reason ? { reason: formData.reason } : {})
        });
        await finish(`${name} has been marked as no-show`);
      },
      failMessage: 'Failed to mark no-show',
      body: (
        <>
          <EmployeeMarkNoShow employee={employee} onValidChange={handleValidChange} />
          <EndEmploymentContracts kind={END_EMPLOYMENT_KINDS.NO_SHOW} contracts={openContracts} />
        </>
      )
    },
    [CONTRACT_ACTIONS.START_DELAYED]: {
      title: `Delay Start Date for ${name}${suffix}`,
      maxWidth: 'md',
      submitText: 'Delay Start',
      onSubmit: async () => {
        await startDelayed({ employeeId: employee.id, ...address, ...formData });
        await finish(`Start date delayed for ${name}`);
      },
      failMessage: 'Failed to delay start date',
      body: <EmployeeStartDelayed employee={employee} contract={contractFacts} onValidChange={handleValidChange} />
    },
    // ADR-158 §7.5 — person verb; see MARK_NO_SHOW above.
    [PERSON_ACTIONS.HIRE_WITHDRAW]: {
      title: `Withdraw Hire for ${name}`,
      maxWidth: 'md',
      submitText: 'Withdraw Hire',
      onSubmit: async () => {
        // ADR-159 §5 — End Employment (HIRE_WITHDRAW); the server withdraws a
        // scheduled end first where one exists.
        await endEmployment({
          kind: END_EMPLOYMENT_KINDS.HIRE_WITHDRAW,
          employeeId: employee.id,
          reason: formData.reason
        });
        await finish(`Hire withdrawn for ${name}`);
      },
      failMessage: 'Failed to withdraw hire',
      body: (
        <>
          <EmployeeHireWithdraw employee={employee} onValidChange={handleValidChange} />
          <EndEmploymentContracts kind={END_EMPLOYMENT_KINDS.HIRE_WITHDRAW} contracts={openContracts} />
        </>
      )
    },
    [CONTRACT_ACTIONS.RESIGN_WITHDRAW]: {
      title: `Withdraw Resignation for ${name}${suffix}`,
      maxWidth: 'md',
      submitText: 'Withdraw Resignation',
      onSubmit: async () => {
        await withdrawResignation({ employeeId: employee.id, ...address, ...formData });
        await finish(`Resignation withdrawn for ${name}`);
      },
      failMessage: 'Failed to withdraw resignation',
      body: (
        <EmployeeResignWithdraw
          employee={employee}
          contract={contractFacts}
          episodeId={episodeId}
          onValidChange={handleValidChange}
        />
      )
    },
    [CONTRACT_ACTIONS.CHANGE_END_DATE]: {
      title: `Change End Date for ${name}${suffix}`,
      maxWidth: 'md',
      submitText: 'Change End Date',
      onSubmit: async (extra = {}) => {
        await changeContractEnd({ employeeId: employee.id, ...address, ...formData, ...extra });
        await finish(`End date changed for ${name}`);
      },
      failMessage: 'Failed to change the end date',
      body: (
        <EmployeeChangeContractEnd
          employee={employee}
          contract={contractFacts}
          episodeId={episodeId}
          onValidChange={handleValidChange}
        />
      )
    },
    [CONTRACT_ACTIONS.CANCEL_CONTRACT]: {
      title: `Cancel Contract for ${name}${suffix}`,
      maxWidth: 'md',
      submitText: 'Cancel Contract',
      onSubmit: async (extra = {}) => {
        await cancelContract({ employeeId: employee.id, ...address, ...formData, ...extra });
        await finish(`Contract cancelled for ${name}`);
      },
      failMessage: 'Failed to cancel the contract',
      body: (
        <EmployeeCancelContract
          employee={employee}
          contract={contractFacts}
          episodeId={episodeId}
          onValidChange={handleValidChange}
        />
      )
    },
    [CONTRACT_ACTIONS.ADD_END_DATE]: {
      title: `Add End Date for ${name}${suffix}`,
      maxWidth: 'md',
      submitText: 'Add End Date',
      onSubmit: async (extra = {}) => {
        // ADR-136 phase 5 (§2a settlement #8) — the open→fixed conversion
        // rides the SAME production resign batch with the generated
        // fixed-term sentence as the reason (the ADR-090 recording path).
        await resign({
          employeeId: employee.id,
          ...address, // Add End Date is contract-addressed (unlike the person verb above)
          resignationDate: formData.endDate,
          resignationReason: formData.resignationReason,
          ...extra
        });
        await finish(`Contract end date added for ${name}`);
      },
      failMessage: 'Failed to add the contract end date',
      body: (
        <EmployeeAddContractEnd
          employee={employee}
          contract={contractFacts}
          episodeId={episodeId}
          onValidChange={handleValidChange}
        />
      )
    },
    // ADR-159 §4.1 (C5) — one not-started contract, never the primary.
    [CONTRACT_ACTIONS.VOID_CONTRACT]: {
      title: `Void Contract for ${name}${suffix}`,
      maxWidth: 'md',
      submitText: 'Void Contract',
      onSubmit: async () => {
        await voidContract({ employeeId: employee.id, employmentEpisodeId: episodeId, reason: formData.reason });
        await finish(`Contract voided for ${name}`);
      },
      failMessage: 'Failed to void the contract',
      body: <EmployeeVoidContract contractLabel={identity} onValidChange={handleValidChange} />
    },
    [CONTRACT_ACTIONS.CANCEL_SCHEDULED_RENEWAL]: {
      title: `Cancel Scheduled Renewal for ${name}${suffix}`,
      maxWidth: 'md',
      submitText: 'Cancel Scheduled Renewal',
      onSubmit: async () => {
        // ADR-155 §4 — cancel BY EVENT: the row the operator is looking at is
        // the event the request names. Null only on a card-launched cancel,
        // which ADR-113 removed from the card; the server then resolves the
        // person's single pending renewal, or refuses.
        const renewalEventId = findPendingRenewalEvent(episode)?.id || null;
        await cancelScheduledRenewal({
          employeeId: employee.id,
          ...address,
          ...(renewalEventId ? { renewalEventId } : {}),
          ...formData
        });
        await finish(`Scheduled replacement cancelled for ${name}`);
      },
      failMessage: 'Failed to cancel the scheduled replacement',
      body: (
        <EmployeeCancelScheduledRenewal
          cutoverOn={findPendingRenewalEvent(episode)?.effectiveOn || contractFacts.pendingRenewalOn}
          onValidChange={handleValidChange}
        />
      )
    }
  };

  const config = CONFIG[action];
  if (!config) return null;

  const handleSubmit = async (extra = {}) => {
    try {
      await config.onSubmit(extra);
      setOutlivingPrompt(null);
    } catch (err) {
      const refusal = err?.data?.refusal || err?.data?.details;
      if (err?.data?.reasonCode === 'SECONDARY_OUTLIVES_PRIMARY' && Array.isArray(refusal?.contracts)) {
        setOutlivingPrompt({ contracts: refusal.contracts });
        return;
      }
      // ADR-171 U6 — the request could not say which contract it meant (the
      // person holds more than one): send the operator where each is listed
      if (err?.data?.code === 'EPISODE_REQUIRED') {
        showAlert(`${name} holds more than one contract — open the Contracts tab and use the action on the contract you mean.`, 'warning', err);
        return;
      }
      showAlert(err.message || config.failMessage, 'error', err);
    }
  };

  const contractLabelById = (id) => {
    const other = (employee.contracts || []).find((c) => c.id === id);
    return other ? contractLineLabel(other) : null;
  };

  if (outlivingPrompt) {
    return (
      <Modal
        isOpen
        onClose={() => setOutlivingPrompt(null)}
        title="End the other contracts too?"
        maxWidth="sm"
        fullWidth
        submitText="End these too"
        cancelText="Back"
        onCancel={() => setOutlivingPrompt(null)}
        onSubmit={() => handleSubmit({ endOtherContractsToo: true })}
      >
        <p style={{ marginTop: 0 }}>
          This is the primary contract, and the primary anchors the employment: no other contract
          may run past its end. These would:
        </p>
        <ul>
          {outlivingPrompt.contracts.map((c) => (
            <li key={c.id}>
              {contractLabelById(c.id) || `Contract from ${c.startDate}`}
              {' — '}
              {c.scheduledEnd ? `ends ${c.scheduledEnd}` : 'open-ended'}
            </li>
          ))}
        </ul>
        <p style={{ marginBottom: 0 }}>
          <strong>End these too</strong> schedules the same end on them in the same step (a contract
          that would only start after it is voided). Or go back and end them first.
        </p>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      onCancel={config.onCancel}
      title={config.title}
      maxWidth={config.maxWidth}
      fullWidth
      submitText={config.submitText}
      cancelText={config.cancelText}
      onSubmit={handleSubmit}
      submitDisabled={!formValid || Boolean(config.blocked)}
    >
      {config.body}
    </Modal>
  );
};

export default ContractActionModals;
